import {
  Box,
  Button,
  Inline,
  Stack,
  useDevice,
  useViewport,
  Viewport,
  DeviceType,
} from '@treatwell/ui';
import clsx from 'clsx';
import {
  LocationSearchInput,
  LocationSearchInputHandle,
} from 'js/components/controls/LocationSearchInput';
import {
  ItemResultData,
  ItemResultType,
} from 'js/components/controls/search-item-result';
import { Context } from 'js/components/LocaleWrapper';
import * as GeoLocation from 'js/helpers/geo-location';
import {
  browseGeolocationValue,
  browseLocationValue,
} from 'js/helpers/uri-util';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { NearMeTreatmentType } from 'js/model/cms/cms-near-me-page';
import { trackSearchLocation } from '../tracking';
import styles from './SearchByCity.module.css';
import barberModel from '../assets/barber-model.webp';
import barberModelDesktop from '../assets/barber-model-desktop.webp';
import beautyModel from '../assets/beauty-model.webp';
import beautyModelDesktop from '../assets/beauty-model-desktop.webp';
import massageModel from '../assets/massage-model.webp';
import massageModelDesktop from '../assets/massage-model-desktop.webp';
import hairModel from '../assets/hair-model.webp';
import hairModelDesktop from '../assets/hair-model-desktop.webp';

const treatmentImageMap: Record<
  NearMeTreatmentType,
  Record<DeviceType, string>
> = {
  barber: {
    mobile: barberModel,
    desktop: barberModelDesktop,
  },
  beauty: {
    mobile: beautyModel,
    desktop: beautyModelDesktop,
  },
  massage: {
    mobile: massageModel,
    desktop: massageModelDesktop,
  },
  hair: {
    mobile: hairModel,
    desktop: hairModelDesktop,
  },
};

const SearchFormBox = ({
  wrapper,
  children,
}: {
  wrapper: (children: React.ReactElement) => React.ReactElement;
  children: React.ReactElement;
}): React.ReactElement => {
  return (
    <Box paddingWide="md" className={styles.searchForm}>
      {wrapper(children)}
    </Box>
  );
};

export const SearchByCity = ({
  renderTitle,
}: {
  renderTitle: (className: string) => React.ReactElement;
}) => {
  const { pageData, generateUri } = useContext(Context);
  const treatment = pageData.nearMe.treatment;
  const locationSearchInputRef = useRef<LocationSearchInputHandle>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isCurrentLocationRejection, setIsCurrentLocationRejection] =
    useState(false);
  const currentLocationSearchData = useRef<ItemResultData | null>(null);
  const isAwaitingCurrentLocation = useRef(false);
  const [isValidLocationSearchInput, setIsValidLocationSearchInput] =
    useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(0);
  const {
    isDesktop: isDesktopDevice,
    isMobile: isMobileDevice,
    type: deviceType,
  } = useDevice();
  const isDesktop = useViewport({
    device: 'desktop',
    serverRender: isDesktopDevice,
  });
  const onLocationSearchInputChange = (
    _value: string,
    _selectedKey: string | null,
    data: ItemResultData | null
  ): void => {
    currentLocationSearchData.current = data;

    if (!isValidLocationSearchInput && data !== null) {
      setIsValidLocationSearchInput(true);
    }
  };
  const goToBrowsePage = async (
    currentPosition: GeolocationPosition | null // eslint-disable-line no-undef
  ) => {
    const browseUriData: Record<string, string> = {
      offerResultType: 'LOCAL',
    };

    let location: Record<string, unknown>;

    if (currentPosition && currentPosition.coords) {
      location = browseGeolocationValue(currentPosition.coords);
    } else if (currentLocationSearchData.current) {
      location = await browseLocationValue(
        pageData,
        currentLocationSearchData.current
      );
    } else {
      location = {
        location: pageData.channel.country.normalisedName,
      };
    }

    browseUriData[pageData.nearMe.browseData.categoryType] =
      pageData.nearMe.browseData.categoryName;

    trackSearchLocation(
      currentLocationSearchData.current?.name ?? (location.location as string)
    ).finally(() => {
      window.location.href = generateUri('browse', {
        ...browseUriData,
        ...location,
      });
    });
  };
  const onSubmit = async () => {
    let isValidLocation = true;
    let currentPosition = null;

    if (isAwaitingCurrentLocation.current) {
      return;
    }

    setIsLoading(true);

    if (
      pageData.cms.page['near-me']['location-required'] === 'true' &&
      !currentLocationSearchData.current
    ) {
      isValidLocation = false;
    } else if (
      currentLocationSearchData.current?.entityType ===
      ItemResultType.CurrentLocation
    ) {
      isAwaitingCurrentLocation.current = true;

      try {
        currentPosition = await GeoLocation.getCurrentPosition();
      } catch (error) {
        if (GeoLocation.isPositionError(error)) {
          if (error.code === error.PERMISSION_DENIED) {
            // eslint-disable-next-line no-alert
            alert(pageData.cms.common.errors['location-blocked']);
          } else {
            // eslint-disable-next-line no-alert
            alert(pageData.cms.common.errors['location-error']);
          }
        }

        setIsCurrentLocationRejection(true);
        locationSearchInputRef.current?.clear();

        isValidLocation = false;
      }

      isAwaitingCurrentLocation.current = false;
    }

    setIsValidLocationSearchInput(isValidLocation);

    if (!isValidLocation) {
      setIsLoading(false);

      return;
    }

    goToBrowsePage(currentPosition);
  };

  useEffect(() => {
    window.navigator.permissions
      .query({ name: 'geolocation' })
      .then((response) => {
        if (response.state === 'denied') {
          setIsCurrentLocationRejection(true);
        }
      });
  }, []);

  useEffect(() => {
    const button = buttonRef.current;

    if (button && isDesktop) {
      setButtonWidth(button.getBoundingClientRect().width);
    } else {
      setButtonWidth(0);
    }
  }, [isDesktop]);

  return (
    <Stack
      space={isDesktop ? 'xxl' : 'xl'}
      justify={isDesktop ? 'center' : 'start'}
      align="center"
      className={clsx(styles.section, styles[treatment])}
    >
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={treatmentImageMap[treatment][deviceType]}
          fetchPriority="high"
        />
      </Helmet>
      <h1 className={styles.title}>{renderTitle(styles.highlight)}</h1>
      <SearchFormBox
        wrapper={(children) => (
          <>
            <Viewport device="mobile" serverRender={isMobileDevice}>
              <Stack space="md">{children}</Stack>
            </Viewport>
            <Viewport
              device={['tablet', 'desktop']}
              serverRender={isDesktopDevice}
            >
              <Inline space="md" align="normal">
                {children}
              </Inline>
            </Viewport>
          </>
        )}
      >
        <>
          <div className={styles.searchInput}>
            <LocationSearchInput
              cmsCommonControls={pageData.cms.common.controls}
              placeholder={pageData.cms.page['near-me']['search-input']}
              isErrorStyling={!isValidLocationSearchInput}
              onChange={onLocationSearchInputChange}
              onSearchResultsChange={() => {}}
              pageData={pageData}
              popularLocations={pageData.nearMe.popularLocations}
              hasCurrentLocationOption={!isCurrentLocationRejection}
              ref={locationSearchInputRef}
            />
          </div>
          <div style={{ minWidth: buttonWidth }}>
            <Button
              className={styles.button}
              buttonStyle="secondary"
              size="md"
              onClick={onSubmit}
              isLoading={isLoading}
              ref={buttonRef}
              data-cy="search-by-city"
              fullWidth
            >
              {pageData.cms.page['near-me']['search-button']}
            </Button>
          </div>
        </>
      </SearchFormBox>
    </Stack>
  );
};
